import type { ReactNode } from "react";
import { Link } from "react-router";

/**
 * ONE ROW OF THE PERSON'S SETTINGS (P206 T0202).
 *
 * The shape is the DECIDED one (D-206-001), not a layout preference: the person opens Settings and
 * meets **the value first**, in his own words, on one page — and the way to change it is chosen by
 * the DATA, not by taste. A setting that is a single value (`Ask me first` · `Steady`) is editable
 * right here; a setting that is an operation (connecting an account, changing his plan, editing his
 * round) states its value and links to the page that owns it. Nothing is duplicated: this page
 * never re-implements an operation that already has a home.
 *
 * THREE RULES ARE ENFORCED BY THIS COMPONENT'S OWN SHAPE:
 *
 * 1. **NO CONTROL THAT CANNOT SAVE.** `control` is only rendered when a caller has a save path. A
 *    section that cannot save renders `value` and says so — a dead control teaches the person that
 *    the product ignores him, which is worse than showing no control at all (D-206-001, and the
 *    retrieval's root-cause finding that buried settings are what make a settings page fail).
 *
 * 2. **THE PERSON ALWAYS KNOWS WHEN A CHANGE LANDED.** `saveState` drives a visible Saved / could
 *    not save line — taken from the one study retrieved for this project, whose finding was exactly
 *    *"users should always know when changes are saved"* (§UX STEP PLANNING Step 0). Silence after
 *    a click is the defect the finding names, so the feedback is part of the primitive rather than
 *    something each section remembers to add.
 *
 * 3. **NO FABRICATED VALUE.** When a read fails or a store does not exist, the caller passes the
 *    honest sentence as `value` (`unavailable`) — this component never renders an empty cell that
 *    reads as "zero" and never renders a placeholder that reads as "nothing to see here".
 */
export type SaveState = "idle" | "saving" | "saved" | "failed";

export interface SettingsSectionProps {
  /** The setting's name in the person's own words — never a DB column, never a mode constant. */
  title: string;
  /** One plain sentence: what this setting decides about what Raisy does for him. */
  hint: string;
  /** What it is set to RIGHT NOW. A real value, or an honest sentence about why there is none. */
  value: ReactNode;
  /** Rendered only where the setting can actually be saved from here. */
  control?: ReactNode;
  /** For operation-shaped settings: the page that owns the action. */
  link?: { to: string; label: string };
  /**
   * Anything the row must SHOW OR OFFER under its value — rendered inside the same card.
   *
   * Row 7 uses it for the account block (avatar, name, language, appearance, sign-in): those are
   * the person's own values and they belong to "My profile", not to a second page that a different
   * viewport happened to serve (P206 T0206).
   */
  detail?: ReactNode;
  saveState?: SaveState;
  saveError?: string | null;
}

export const SettingsSection = ({
  title,
  hint,
  value,
  control,
  link,
  detail,
  saveState = "idle",
  saveError,
}: SettingsSectionProps) => (
  <section className="rounded-lg border border-border bg-card p-5">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
      </div>
      <div className="flex min-w-0 flex-col items-start gap-2 sm:items-end">
        <div className="text-sm font-medium text-foreground">{value}</div>
        {control}
        <div aria-live="polite" className="min-h-[1.25rem] text-xs">
          {saveState === "saving" && <span className="text-muted-foreground">Saving…</span>}
          {saveState === "saved" && <span className="text-emerald-600">Saved</span>}
          {saveState === "failed" && (
            <span className="text-amber-700">
              Raisy could not save this just now
              {saveError ? ` — ${saveError}` : ""}. Your previous setting still stands.
            </span>
          )}
        </div>
      </div>
    </div>
    {link && (
      <div className="mt-3">
        <Link to={link.to} className="text-sm font-medium text-primary underline-offset-4 hover:underline">
          {link.label} →
        </Link>
      </div>
    )}
    {detail && (
      <div className="mt-4 border-t border-border pt-4">
        {detail}
      </div>
    )}
  </section>
);
